
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface JournalEntryProps {
  entry: {
    created_at: string;
    content: string;
    mood_rating: string;
    mood_tags?: string[];
    ai_verse?: string;
    ai_prayer?: string;
  };
}

export function JournalEntry({ entry }: JournalEntryProps) {
  const getMoodLabel = (rating: string) => {
    const numRating = parseInt(rating);
    if (numRating <= 2) return "Challenging";
    if (numRating === 3) return "Neutral";
    return "Blessed";
  };

  const getMoodColor = (rating: string) => {
    const numRating = parseInt(rating);
    if (numRating <= 2) return "text-red-300 bg-red-900/30";
    if (numRating === 3) return "text-gray-300 bg-gray-800/30";
    return "text-green-300 bg-green-900/30";
  };

  return (
    <Card className="group hover:shadow-sm transition-all duration-300 border-[#333344] bg-[#1A1A25]">
      <CardContent className="pt-4 pb-3 px-3 space-y-2">
        <div className="flex justify-between items-start gap-2 mb-1">
          <span className="text-xs text-gray-400">
            {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
          </span>
          <Badge variant="secondary" className={`${getMoodColor(entry.mood_rating)} px-2 py-0.5 text-xs`}>
            {getMoodLabel(entry.mood_rating)}
          </Badge>
        </div>

        <p className="text-xs whitespace-pre-wrap leading-relaxed text-gray-200">
          {entry.content}
        </p>
        
        {entry.mood_tags && entry.mood_tags.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {entry.mood_tags.map((tag) => (
              <span
                key={tag}
                className="px-1.5 py-0.5 text-xs rounded-full bg-[#2A2A35] text-gray-200 font-medium text-[10px]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {(entry.ai_verse || entry.ai_prayer) && (
          <div className="mt-2 space-y-1.5 pt-2 border-t border-[#333344]">
            {entry.ai_verse && (
              <div className="text-xs italic text-gray-300">
                <span className="font-semibold text-[10px]">Verse: </span>
                {entry.ai_verse}
              </div>
            )}
            {entry.ai_prayer && (
              <div className="text-xs text-gray-300">
                <span className="font-semibold text-[10px]">Prayer: </span>
                {entry.ai_prayer}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
